/**
 * Goals Tab — structured by time horizon: Annual → Quarterly → Monthly → Weekly.
 * Each goal has: title, target_amount, due_date, status, cycle (timeframe).
 * Auto-assigns timeframe from due_date with manual override.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FetchErrorState } from "../components/FetchErrorState";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Trash2, Target, Calendar, CheckCircle2, RefreshCw, Link2 } from "lucide-react";
import { ConversationPrompt } from "../components/privacy/ConversationPrompt";
import { PrivacyBadge } from "../components/privacy/PrivacyBadge";
import { usePrivacy } from "../components/privacy/PrivacyProvider";
import { GoalMomentum } from "../components/guidance/GoalMomentum";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  in_progress: { label: "In Progress", bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-700 dark:text-blue-300", icon: Target },
  complete: { label: "Complete", bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-700 dark:text-emerald-300", icon: CheckCircle2 },
  changed: { label: "Changed", bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-700 dark:text-amber-300", icon: RefreshCw },
  active: { label: "In Progress", bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-700 dark:text-blue-300", icon: Target },
};

const TIMEFRAMES = [
  { key: "annual", label: "Annual Goals", sub: "The bigger picture for this year" },
  { key: "quarterly", label: "Quarterly Goals", sub: "Focus areas for the next few months" },
  { key: "monthly", label: "Monthly Goals", sub: "What matters this month" },
  { key: "weekly", label: "Weekly Goals", sub: "What you're doing this week" },
];

const CYCLE_LABELS = { annual: "Annual", quarterly: "Quarterly", monthly: "Monthly", weekly: "Weekly" };
const CYCLE_COLORS = {
  annual: "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300",
  quarterly: "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300",
  monthly: "bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300",
  weekly: "bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300",
};

const CYCLE_ORDER = { annual: 0, quarterly: 1, monthly: 2, weekly: 3 };

/** Returns goals from higher (bigger) timeframes that can be parents */
function getParentCandidates(allGoals, currentCycle, currentGoalId) {
  const currentOrder = CYCLE_ORDER[currentCycle] ?? 2;
  return allGoals.filter(g =>
    g.goal_id !== currentGoalId &&
    (CYCLE_ORDER[g.cycle] ?? 2) < currentOrder &&
    (g.status === "in_progress" || g.status === "active")
  );
}

function formatCurrency(val, currency = "NZD") {
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
}

function formatDateShort(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
}

/** Infer timeframe from due_date relative to today */
function inferTimeframe(dueDate) {
  if (!dueDate) return "monthly";
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays <= 7) return "weekly";
  if (diffDays <= 31) return "monthly";
  if (diffDays <= 93) return "quarterly";
  return "annual";
}

// ========== Reflection Prompt (100% milestone) ==========
const _dismissedReflections = new Set();

function ReflectionPrompt({ goal }) {
  const { saveJournal } = usePrivacy();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dismissed, setDismissed] = useState(_dismissedReflections.has(goal.goal_id));

  const milestones = goal.milestones_achieved || [];
  if (!milestones.includes(100) || dismissed || saved) return null;

  const handleSave = async () => {
    if (!note.trim()) return;
    setSaving(true);
    await saveJournal(note.trim(), `Reflection on reaching: ${goal.description}`, goal.visibility, goal.goal_id);
    setSaving(false);
    setSaved(true);
    toast("Reflection saved", { duration: 3000 });
  };

  const handleDismiss = () => {
    _dismissedReflections.add(goal.goal_id);
    setDismissed(true);
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800" data-testid={`reflection-prompt-${goal.goal_id}`}>
      <p className="text-xs text-muted-foreground mb-2">Want to capture what this means?</p>
      <div className="flex gap-2">
        <Input
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="A few words..."
          className="text-xs h-8"
          data-testid={`reflection-input-${goal.goal_id}`}
          onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
        />
        <Button size="sm" variant="ghost" className="h-8 text-xs px-3" onClick={handleSave} disabled={saving || !note.trim()} data-testid={`reflection-save-${goal.goal_id}`}>
          Save
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-400 px-2" onClick={handleDismiss} data-testid={`reflection-skip-${goal.goal_id}`}>
          Skip
        </Button>
      </div>
    </div>
  );
}

// ========== Goal Card ==========
function GoalCard({ goal, currency, allGoals, onEdit, onDelete, onStatusChange, showPrivacyBadge }) {
  const cfg = STATUS_CONFIG[goal.status] || STATUS_CONFIG.in_progress;
  const StatusIcon = cfg.icon;
  const dueDate = formatDateShort(goal.due_date);
  const parentGoal = goal.parent_goal_id ? allGoals.find(g => g.goal_id === goal.parent_goal_id) : null;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-slate-300 dark:hover:border-slate-700 transition-colors" data-testid={`goal-card-${goal.goal_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            <StatusIcon className="w-3 h-3" />
            {cfg.label}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CYCLE_COLORS[goal.cycle] || CYCLE_COLORS.monthly}`} data-testid={`goal-timeframe-${goal.goal_id}`}>
            {CYCLE_LABELS[goal.cycle] || "Monthly"} goal
          </span>
          {showPrivacyBadge && <PrivacyBadge visibility={goal.visibility || "shared"} />}
          {dueDate && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <Calendar className="w-3 h-3" /> {dueDate}
            </span>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`goal-menu-${goal.goal_id}`}>
              <MoreHorizontal className="w-4 h-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onEdit(goal)}>
              <Pencil className="w-4 h-4 mr-2" />Edit
            </DropdownMenuItem>
            {goal.status !== "complete" && (
              <DropdownMenuItem onClick={() => onStatusChange(goal, "complete")}>
                <CheckCircle2 className="w-4 h-4 mr-2" />Mark Complete
              </DropdownMenuItem>
            )}
            {goal.status !== "changed" && (
              <DropdownMenuItem onClick={() => onStatusChange(goal, "changed")}>
                <RefreshCw className="w-4 h-4 mr-2" />Mark Changed
              </DropdownMenuItem>
            )}
            {goal.status !== "in_progress" && (
              <DropdownMenuItem onClick={() => onStatusChange(goal, "in_progress")}>
                <Target className="w-4 h-4 mr-2" />Back to In Progress
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onDelete(goal)} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-1">{goal.description}</h3>

      {goal.target_amount != null && (
        <p className="text-sm tabular-nums text-slate-600 dark:text-slate-400">
          Target: {formatCurrency(goal.target_amount, currency)}
        </p>
      )}

      {goal.notes && (
        <p className="text-xs text-slate-400 mt-2 line-clamp-2">{goal.notes}</p>
      )}

      {parentGoal && (
        <button
          type="button"
          onClick={() => {
            const el = document.querySelector(`[data-testid="goal-card-${parentGoal.goal_id}"]`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.classList.add("ring-2", "ring-emerald-400", "ring-offset-2");
              setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400", "ring-offset-2"), 2000);
            }
          }}
          className="text-xs text-slate-400 mt-2 flex items-center gap-1.5 hover:text-slate-600 dark:hover:text-slate-300 transition-colors group/link cursor-pointer"
          data-testid={`goal-parent-${goal.goal_id}`}
        >
          <Link2 className="w-3 h-3 flex-shrink-0" />
          Working toward: <span className="font-medium text-slate-500 dark:text-slate-400 group-hover/link:text-emerald-600 dark:group-hover/link:text-emerald-400 transition-colors">{parentGoal.description}</span>
        </button>
      )}

      <GoalMomentum goal={goal} />
      <ReflectionPrompt goal={goal} />
    </div>
  );
}

// ========== Time Horizon Section ==========
function HorizonSection({ tf, goals, allGoals, currency, onAdd, onEdit, onDelete, onStatusChange, showPrivacyBadge }) {
  const inProgress = goals.filter(g => g.status === "in_progress" || g.status === "active").length;
  const complete = goals.filter(g => g.status === "complete").length;
  const sorted = [...goals].sort((a, b) => {
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });

  return (
    <section data-testid={`goals-section-${tf.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{tf.label}</h2>
          <p className="text-xs text-slate-400 mt-0.5">{tf.sub}</p>
        </div>
        <div className="flex items-center gap-3">
          {goals.length > 0 && (
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span>{goals.length} total</span>
              <span className="text-blue-600">{inProgress} active</span>
              {complete > 0 && <span className="text-emerald-600">{complete} done</span>}
            </div>
          )}
          <Button onClick={() => onAdd(tf.key)} size="sm" variant="ghost" className="text-slate-500 hover:text-slate-700 h-8" data-testid={`add-${tf.key}-goal-btn`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add
          </Button>
        </div>
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
          <p className="text-sm text-muted-foreground mb-1">No {tf.key} goals yet</p>
          <p className="text-xs text-muted-foreground/60 mb-3">Start with one goal that matters right now.</p>
          <Button onClick={() => onAdd(tf.key)} variant="outline" size="sm" className="rounded-full">
            <Plus className="w-3.5 h-3.5 mr-1" />Add a goal
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sorted.map(g => (
            <GoalCard key={g.goal_id} goal={g} currency={currency} allGoals={allGoals} onEdit={onEdit} onDelete={onDelete} onStatusChange={onStatusChange} showPrivacyBadge={showPrivacyBadge} />
          ))}
        </div>
      )}
    </section>
  );
}

// ========== Goal Dialog ==========
function GoalDialog({ open, onOpenChange, goal, defaultCycle, allGoals, onSave, loading }) {
  const isEdit = !!goal?.goal_id;
  const { hasPersonal, isSeparate } = usePrivacy();
  const showVisibility = hasPersonal || isSeparate;
  const [form, setForm] = useState({ description: "", target_amount: "", due_date: "", notes: "", status: "in_progress", cycle: "monthly", parent_goal_id: "", visibility: "shared" });

  useEffect(() => {
    if (goal?.goal_id) {
      setForm({
        description: goal.description || "", target_amount: goal.target_amount ?? "",
        due_date: goal.due_date || "", notes: goal.notes || "",
        status: goal.status || "in_progress", cycle: goal.cycle || "monthly",
        parent_goal_id: goal.parent_goal_id || "",
        visibility: goal.visibility || "shared",
      });
    } else {
      setForm({ description: "", target_amount: "", due_date: "", notes: "", status: "in_progress", cycle: defaultCycle || "monthly", parent_goal_id: "", visibility: "shared" });
    }
  }, [goal, open, defaultCycle]);

  // Auto-infer timeframe when due_date changes — always suggest, user can override
  const inferredCycle = form.due_date ? inferTimeframe(form.due_date) : null;
  const handleDueDateChange = (newDate) => {
    const inferred = inferTimeframe(newDate);
    setForm(p => ({ ...p, due_date: newDate, cycle: inferred }));
  };

  const submit = () => {
    if (!form.description.trim()) { toast.error("Give your goal a name"); return; }
    if (!form.due_date) { toast.error("Add a due date to keep this on track"); return; }
    onSave({
      description: form.description.trim(), target_amount: form.target_amount ? parseFloat(form.target_amount) : null,
      due_date: form.due_date, notes: form.notes.trim() || null, status: form.status,
      cycle: form.cycle, parent_goal_id: form.parent_goal_id || null, goal_id: goal?.goal_id,
      visibility: form.visibility,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="goal-dlg-desc">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit Goal" : "Create Goal"}</DialogTitle></DialogHeader>
        <p id="goal-dlg-desc" className="sr-only">Goal form</p>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-xs text-slate-500">Title *</Label>
            <Input placeholder="e.g., Build $5,000 emergency fund" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="goal-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Target Amount</Label>
              <Input type="number" min="0" placeholder="Optional" value={form.target_amount} onChange={e => setForm(p => ({ ...p, target_amount: e.target.value }))} data-testid="goal-amount-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Due Date *</Label>
              <Input type="date" value={form.due_date} onChange={e => handleDueDateChange(e.target.value)} data-testid="goal-due-date-input" />
              {inferredCycle && form.due_date && (
                <p className="text-xs text-slate-400 mt-1" data-testid="due-date-hint">
                  Based on your due date, this is a <span className="font-medium text-slate-600 dark:text-slate-300">{CYCLE_LABELS[inferredCycle]}</span> goal
                </p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Timeframe</Label>
            <Select value={form.cycle} onValueChange={v => setForm(p => ({ ...p, cycle: v }))}>
              <SelectTrigger data-testid="goal-cycle-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Linked to bigger goal — only show for non-annual goals */}
          {form.cycle !== "annual" && (() => {
            const candidates = getParentCandidates(allGoals || [], form.cycle, goal?.goal_id);
            if (candidates.length === 0) return null;
            return (
              <div>
                <Label className="text-xs text-slate-500">Linked to bigger goal <span className="text-slate-400">(optional)</span></Label>
                <Select value={form.parent_goal_id || "none"} onValueChange={v => setForm(p => ({ ...p, parent_goal_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="goal-parent-select"><SelectValue placeholder="No link" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none"><span className="text-slate-400">No link</span></SelectItem>
                    {candidates.map(c => (
                      <SelectItem key={c.goal_id} value={c.goal_id}>
                        <span className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CYCLE_COLORS[c.cycle] || ""}`}>{CYCLE_LABELS[c.cycle]}</span>
                          {c.description}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })()}
          {isEdit && (
            <div>
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="goal-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="changed">Changed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {showVisibility && (
            <div>
              <Label className="text-xs text-slate-500">Visibility</Label>
              <Select value={form.visibility} onValueChange={v => setForm(p => ({ ...p, visibility: v }))}>
                <SelectTrigger data-testid="goal-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Shared — visible to household</SelectItem>
                  <SelectItem value="personal">Personal — only you can see this</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">Notes</Label>
            <Input placeholder="Optional notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} data-testid="goal-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="save-goal-btn">
            {loading && <span className="mr-2 animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full" />}
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== Main Content ==========
export function GoalsContent() {
  const { household } = useAuth();
  const { hasPersonal, isSeparate, privacyStyle } = usePrivacy();
  const showPrivacyBadge = hasPersonal || isSeparate;

  const getMilestoneToast = useCallback((milestone) => {
    if (milestone === 100) {
      if (privacyStyle === "solo") return "You've reached this";
      if (privacyStyle === "separate_shared_goals") return "This goal is moving forward";
      return "You've reached this together";
    }
    if (privacyStyle === "solo") return "You're making this happen";
    if (privacyStyle === "separate_shared_goals") return "This goal is moving forward";
    return "You're moving this forward together";
  }, [privacyStyle]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [defaultCycle, setDefaultCycle] = useState("monthly");

  const currency = household?.currency || "NZD";

  const fetchGoals = useCallback(async () => {
    if (!household?.household_id) { setLoading(false); return; }
    setFetchError(false);
    console.log("[Goals] Fetching for household:", household.household_id);
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/workflow-goals`, { credentials: "include" });
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        console.error(`[Goals] Fetch failed: ${res.status}`, errText);
        setFetchError(true);
        if (res.status === 401) { toast.error("Session expired — please log in again."); }
        return;
      }
      const data = await res.json();
      console.log("[Goals] Loaded:", data.goals?.length || 0, "goals");
      setGoals(data.goals || []);
    } catch (e) {
      console.error("[Goals] Network error:", e);
      setFetchError(true);
    } finally { setLoading(false); }
  }, [household?.household_id]);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  const openAdd = (cycle) => { setDefaultCycle(cycle || "monthly"); setEditingGoal(null); setDialogOpen(true); };
  const openEdit = (g) => { setEditingGoal(g); setDialogOpen(true); };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const isEdit = !!data.goal_id;
      const url = isEdit
        ? `${API_URL}/api/households/${household.household_id}/workflow-goals/${data.goal_id}`
        : `${API_URL}/api/households/${household.household_id}/workflow-goals`;
      const body = isEdit
        ? { description: data.description, target_amount: data.target_amount, due_date: data.due_date, notes: data.notes, status: data.status, cycle: data.cycle, parent_goal_id: data.parent_goal_id }
        : { description: data.description, target_amount: data.target_amount, due_date: data.due_date, notes: data.notes, cycle: data.cycle, parent_goal_id: data.parent_goal_id };
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        if (res.status === 401) { toast.error("Session expired. Please refresh and log in again."); return; }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      const result = await res.json();
      if (result.milestone_event) {
        toast(getMilestoneToast(result.milestone_event.milestone), { duration: 4000 });
      } else {
        toast.success(isEdit ? "Goal updated" : "You've given your money a direction");
      }
      setDialogOpen(false); setEditingGoal(null);
      fetchGoals();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleDelete = async (goal) => {
    if (!window.confirm(`Delete "${goal.description}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/workflow-goals/${goal.goal_id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Goal deleted"); fetchGoals();
    } catch (e) { toast.error(e.message); }
  };

  const handleStatusChange = async (goal, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/workflow-goals/${goal.goal_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Update failed");
      const result = await res.json();
      if (result.milestone_event) {
        toast(getMilestoneToast(result.milestone_event.milestone), { duration: 4000 });
      } else {
        toast.success(`Goal marked as ${newStatus.replace("_", " ")}`);
      }
      fetchGoals();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto">
        <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-28 bg-white dark:bg-slate-900 rounded-xl animate-pulse" />)}</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto">
        <FetchErrorState onRetry={fetchGoals} message="We couldn't load your goals. Your data is safe — this is just a connection issue." />
      </div>
    );
  }

  // Group goals by cycle/timeframe
  const grouped = { annual: [], quarterly: [], monthly: [], weekly: [] };
  goals.forEach(g => {
    const c = g.cycle || "monthly";
    if (grouped[c]) grouped[c].push(g);
    else grouped.monthly.push(g);
  });

  const totalGoals = goals.length;
  const inProgressCount = goals.filter(g => g.status === "in_progress" || g.status === "active").length;
  const completeCount = goals.filter(g => g.status === "complete").length;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto space-y-8" data-testid="goals-content">
      {/* Conversation Prompt */}
      <ConversationPrompt promptKey="goals" showJournal={true} />

      {/* Summary */}
      {totalGoals > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Total Goals</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{totalGoals}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">In Progress</p>
            <p className="text-2xl font-semibold text-blue-600">{inProgressCount}</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Complete</p>
            <p className="text-2xl font-semibold text-emerald-600">{completeCount}</p>
          </div>
        </div>
      )}

      {/* Empty state — no goals at all */}
      {totalGoals === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl" data-testid="goals-empty">
          <Target className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium mb-1">Start with one goal that matters right now</p>
          <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
            Give your money a direction. Set a target, add a due date, and track your progress.
          </p>
          <Button onClick={() => openAdd("monthly")} variant="outline" size="sm" data-testid="add-first-goal-btn">Add a goal</Button>
        </div>
      )}

      {/* Time horizon sections */}
      {totalGoals > 0 && TIMEFRAMES.map(tf => (
        <HorizonSection
          key={tf.key}
          tf={tf}
          goals={grouped[tf.key]}
          allGoals={goals}
          currency={currency}
          onAdd={openAdd}
          onEdit={openEdit}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          showPrivacyBadge={showPrivacyBadge}
        />
      ))}

      <GoalDialog open={dialogOpen} onOpenChange={setDialogOpen} goal={editingGoal} defaultCycle={defaultCycle} allGoals={goals} onSave={handleSave} loading={saving} />
    </div>
  );
}
